import * as i0 from '@angular/core';
import { Directive, HostListener, NgModule, Injectable, Input, Host, Optional, HostBinding, Component, ChangeDetectionStrategy, InjectionToken, APP_INITIALIZER, NgZone } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import * as i2 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { __decorate } from 'tslib';
import { fromEvent } from 'rxjs';
import { applyPolyfills, defineCustomElements } from '@markant/elements/dist/loader';

class ValueAccessorDirective {
    constructor(el) {
        this.el = el;
        this.onChange = () => { };
        this.onTouched = () => { };
    }
    writeValue(value) {
        this.el.nativeElement.value = this.lastValue = value == null ? '' : value;
    }
    setDisabledState(isDisabled) {
        this.el.nativeElement.disabled = Boolean(isDisabled);
    }
    handleChangeEvent(value) {
        // MEL-134: this non-strict compare allows number values to be used via
        // the FormControl. Otherwise the string to number difference triggers
        // onChange and marks the FormControl unintentional as dirty
        if (value != this.lastValue || (value === 0 && this.lastValue === '')) {
            this.lastValue = value;
            this.onChange(value);
            this.writeValue(value);
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    handleBlurEvent() {
        this.onTouched();
    }
}

class BooleanValueAccessorDirective extends ValueAccessorDirective {
    constructor(el, renderer) {
        super(el);
        this.renderer = renderer;
    }
    _handleCheckedChange(value) {
        this.handleChangeEvent(value);
    }
    writeValue(value) {
        this.el.nativeElement.checked = this.lastValue = value == null ? '' : value;
    }
}
/** @nocollapse */ BooleanValueAccessorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: BooleanValueAccessorDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ BooleanValueAccessorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: BooleanValueAccessorDirective, selector: "markant-checkbox,markant-segmented-control[markant-role=\"checkbox\"],markant-switch", host: { listeners: { "checkedChange": "_handleCheckedChange($event.detail)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: BooleanValueAccessorDirective,
            multi: true
        }
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: BooleanValueAccessorDirective, decorators: [{
            type: Directive,
            args: [{
                    /* tslint:disable-next-line:directive-selector */
                    selector: 'markant-checkbox,markant-segmented-control[markant-role="checkbox"],markant-switch',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: BooleanValueAccessorDirective,
                            multi: true
                        }
                    ]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { _handleCheckedChange: [{
                type: HostListener,
                args: ['checkedChange', ['$event.detail']]
            }] } });

class FsValueAccessorDirective extends ValueAccessorDirective {
    constructor(el) {
        super(el);
    }
    _handleInputEvent(value) {
        if (this.el.nativeElement.multiple) {
            this.handleChangeEvent(value.files);
        }
        else {
            this.handleChangeEvent(value.files[0]);
        }
    }
    writeValue(value) {
        if (value instanceof FileList) {
            this.el.nativeElement.files = value;
        }
        else if (Array.isArray(value) && !value.length) {
            this.el.nativeElement.files = null;
        }
        else if (value === null) {
            this.el.nativeElement.files = null;
        }
        else {
            // Since we cannot manually construct a FileList instance, we have to ignore
            // any attempt to push a non-FileList instance into the input.
            if (console && console.warn && console.log) {
                console.warn('Ignoring attempt to assign non-FileList to input[type=file].');
                console.log('Value:', value);
            }
        }
    }
}
/** @nocollapse */ FsValueAccessorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: FsValueAccessorDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ FsValueAccessorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: FsValueAccessorDirective, selector: "markant-input-file,input[type=file]", host: { listeners: { "changeFile": "_handleInputEvent($event.detail)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FsValueAccessorDirective,
            multi: true
        }
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: FsValueAccessorDirective, decorators: [{
            type: Directive,
            args: [{
                    /* tslint:disable-next-line:directive-selector */
                    selector: 'markant-input-file,input[type=file]',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: FsValueAccessorDirective,
                            multi: true
                        }
                    ]
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { _handleInputEvent: [{
                type: HostListener,
                args: ['changeFile', ['$event.detail']]
            }] } });

/*** Internal-only NgModule that works as a host for the `RadioControlRegistry` tree-shakable* provider.*/
class RadioControlRegistryModule {
}
/** @nocollapse */ RadioControlRegistryModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioControlRegistryModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ RadioControlRegistryModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.10", ngImport: i0, type: RadioControlRegistryModule });
/** @nocollapse */ RadioControlRegistryModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioControlRegistryModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioControlRegistryModule, decorators: [{
            type: NgModule
        }] });
/***
 * @description* Class used by Angular to track radio buttons. For internal use only.* This is adopted from the original angular RadioControlValueAccessor
 *
 * see: https://github.com/angular/angular/blob/1c11a5715576632a4fb7170202395cf95dfbce09/packages/forms/src/directives/radio_control_value_accessor.ts*/
class RadioControlRegistry {
    constructor() {
        this._accessors = [];
    }
    /**
     * @description
     * Adds a control to the internal registry. For internal use only.
     */
    add(control, accessor) {
        this._accessors.push([control, accessor]);
    }
    /**
     * @description
     * Removes a control from the internal registry. For internal use only.
     */
    remove(accessor) {
        for (let i = this._accessors.length - 1; i >= 0; --i) {
            if (this._accessors[i][1] === accessor) {
                this._accessors.splice(i, 1);
                return;
            }
        }
    }
    /**
     * @description
     * Selects a radio button. For internal use only.
     * Fires uncheck on all other radio buttons belonging to the same group
     */
    select(accessor) {
        this._accessors.forEach((c) => {
            if (this._isSameGroup(c, accessor) && c[1] !== accessor) {
                c[1].fireUncheck(accessor.value);
            }
        });
    }
    _isSameGroup(controlPair, accessor) {
        if (!controlPair[0].control)
            return false;
        return (controlPair[0].control.parent ===
            accessor._control.control._parent &&
            (controlPair[1].name === accessor.name ||
                controlPair[0].name === accessor.formControlName));
    }
}
/** @nocollapse */ RadioControlRegistry.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioControlRegistry, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
/** @nocollapse */ RadioControlRegistry.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioControlRegistry, providedIn: RadioControlRegistryModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioControlRegistry, decorators: [{
            type: Injectable,
            args: [{ providedIn: RadioControlRegistryModule }]
        }] });
class RadioValueAccessorDirective extends ValueAccessorDirective {
    constructor(el, _registry, _injector) {
        super(el);
        this._registry = _registry;
        this._injector = _injector;
    }
    ngOnInit() {
        this._control = this._injector.get(NgControl);
        this._registry.add(this._control, this);
    }
    ngOnDestroy() {
        this._registry.remove(this);
    }
    _handleCheckedChange(event) {
        this.onChange(event.target.value);
    }
    writeValue(value) {
        this._state = value == this.value;
        this.el.nativeElement.checked = this._state;
    }
    /**
     * Registers a function called when the control value changes.
     * @nodoc
     */
    registerOnChange(fn) {
        this._fn = fn;
        this.onChange = () => {
            fn(this.value);
            this._registry.select(this);
            this.writeValue(this.value);
        };
    }
    fireUncheck(value) {
        this.writeValue(value);
    }
}
/** @nocollapse */ RadioValueAccessorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioValueAccessorDirective, deps: [{ token: i0.ElementRef }, { token: RadioControlRegistry }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ RadioValueAccessorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: RadioValueAccessorDirective, selector: "markant-radio[formControlName],markant-radio[formControl],markant-radio[ngModel],markant-segmented-control[formControlName],markant-segmented-control[formControl],markant-segmented-control[ngModel]", inputs: { value: "value", name: "name", formControlName: "formControlName" }, host: { listeners: { "checkedChange": "_handleCheckedChange($event)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: RadioValueAccessorDirective,
            multi: true,
        },
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: RadioValueAccessorDirective, decorators: [{
            type: Directive,
            args: [{
                    /* tslint:disable-next-line:directive-selector */
                    selector: "markant-radio[formControlName],markant-radio[formControl],markant-radio[ngModel],markant-segmented-control[formControlName],markant-segmented-control[formControl],markant-segmented-control[ngModel]",
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: RadioValueAccessorDirective,
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: RadioControlRegistry }, { type: i0.Injector }]; }, propDecorators: { value: [{
                type: Input
            }], name: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], _handleCheckedChange: [{
                type: HostListener,
                args: ["checkedChange", ["$event"]]
            }] } });

class TextValueAccessorDirective extends ValueAccessorDirective {
    constructor(el) {
        super(el);
    }
    _handleInputEvent(value) {
        this.handleChangeEvent(value);
    }
    _handleMarkantBlur() {
        this.handleBlurEvent();
    }
}
/** @nocollapse */ TextValueAccessorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: TextValueAccessorDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ TextValueAccessorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: TextValueAccessorDirective, selector: "markant-input,markant-input-new,markant-currency-input,markant-textarea,markant-range,markant-select,markant-datepicker,markant-input-number,markant-searchbar", host: { listeners: { "valueChange": "_handleInputEvent($event.detail)", "markantBlur": "_handleMarkantBlur($event)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: TextValueAccessorDirective,
            multi: true,
        },
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: TextValueAccessorDirective, decorators: [{
            type: Directive,
            args: [{
                    /* tslint:disable-next-line:directive-selector */
                    selector: 'markant-input,markant-input-new,markant-currency-input,markant-textarea,markant-range,markant-select,markant-datepicker,markant-input-number,markant-searchbar',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: TextValueAccessorDirective,
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { _handleInputEvent: [{
                type: HostListener,
                args: ['valueChange', ['$event.detail']]
            }], _handleMarkantBlur: [{
                type: HostListener,
                args: ['markantBlur', ['$event']]
            }] } });

class InputDividedValueAccessorDirective extends ValueAccessorDirective {
    constructor(el) {
        super(el);
    }
    writeValue(value) {
        this.el.nativeElement.value = this.lastValue = value;
    }
    _handleInputEvent(value) {
        this.handleChangeEvent(value);
    }
}
/** @nocollapse */ InputDividedValueAccessorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: InputDividedValueAccessorDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ InputDividedValueAccessorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: InputDividedValueAccessorDirective, selector: "markant-input-divided", host: { listeners: { "valueChange": "_handleInputEvent($event.detail)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: InputDividedValueAccessorDirective,
            multi: true,
        },
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: InputDividedValueAccessorDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line
                    selector: 'markant-input-divided',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: InputDividedValueAccessorDirective,
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { _handleInputEvent: [{
                type: HostListener,
                args: ['valueChange', ['$event.detail']]
            }] } });

class CombiSelectValueAccessorDirective extends ValueAccessorDirective {
    constructor(el) {
        super(el);
    }
    _handleInputEvent(value) {
        this.handleChangeEvent(value);
    }
    _handleMarkantBlur() {
        this.handleBlurEvent();
    }
}
/** @nocollapse */ CombiSelectValueAccessorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: CombiSelectValueAccessorDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ CombiSelectValueAccessorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: CombiSelectValueAccessorDirective, selector: "markant-combi-select-new", host: { listeners: { "selectOption": "_handleInputEvent($event.detail)", "valueChange": "_handleInputEvent($event.detail)", "markantBlur": "_handleMarkantBlur($event)" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: CombiSelectValueAccessorDirective,
            multi: true,
        },
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: CombiSelectValueAccessorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'markant-combi-select-new',
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: CombiSelectValueAccessorDirective,
                            multi: true,
                        },
                    ],
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { _handleInputEvent: [{
                type: HostListener,
                args: ['selectOption', ['$event.detail']]
            }, {
                type: HostListener,
                args: ['valueChange', ['$event.detail']]
            }], _handleMarkantBlur: [{
                type: HostListener,
                args: ['markantBlur', ['$event']]
            }] } });

/**
 * Helper directive to properly handle router events with markant-nav-item.
 *
 * Syncs an (if given) `routerLink` directive with the `link-to` property to
 * ensure native click behaviour (open in new tab, ...).
 *
 * Mainly derived from
 * https://github.com/angular/angular/blob/2c269c390a15b04419e176674a8af46e0ce29907/packages/router/src/directives/router_link.ts.
 */
class MarkantNavItemDirective {
    constructor(router, locationStrategy, routerLinkDirective) {
        this.router = router;
        this.locationStrategy = locationStrategy;
        this.routerLinkDirective = routerLinkDirective;
        this.subscription = router.events.subscribe(s => {
            if (s instanceof NavigationEnd) {
                this.maybeUpdateUrl();
            }
        });
    }
    onClick(button, ctrlKey, shiftKey, altKey, metaKey) {
        if (button !== 0 || ctrlKey || shiftKey || altKey || metaKey) {
            return true;
        }
        if (typeof this.target === 'string' && this.target !== '_self') {
            return true;
        }
        if (this.routerLinkDirective === null) {
            return true;
        }
        const { skipLocationChange, replaceUrl, urlTree, state } = this.routerLinkDirective;
        this.router.navigateByUrl(urlTree, {
            skipLocationChange: attrBoolValue$1(skipLocationChange),
            replaceUrl: attrBoolValue$1(replaceUrl),
            state: state
        });
        return false;
    }
    ngOnChanges() {
        this.maybeUpdateUrl();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    maybeUpdateUrl() {
        if (this.routerLinkDirective !== null) {
            const urlTree = this.routerLinkDirective.urlTree;
            this.linkTo = urlTree !== null ? this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(urlTree))
                : null;
        }
    }
}
/** @nocollapse */ MarkantNavItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavItemDirective, deps: [{ token: i1.Router }, { token: i2.LocationStrategy }, { token: i1.RouterLink, host: true, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ MarkantNavItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: MarkantNavItemDirective, selector: "markant-nav-item", inputs: { target: "target", linkTo: "linkTo" }, host: { listeners: { "click": "onClick($event.button,$event.ctrlKey,$event.shiftKey,$event.altKey,$event.metaKey)" }, properties: { "attr.target": "this.target", "attr.link-to": "this.linkTo" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'markant-nav-item'
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2.LocationStrategy }, { type: i1.RouterLink, decorators: [{
                    type: Host
                }, {
                    type: Optional
                }] }]; }, propDecorators: { target: [{
                type: HostBinding,
                args: ['attr.target']
            }, {
                type: Input
            }], linkTo: [{
                type: HostBinding,
                args: ['attr.link-to']
            }, {
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', [
                        '$event.button',
                        '$event.ctrlKey',
                        '$event.shiftKey',
                        '$event.altKey',
                        '$event.metaKey'
                    ]]
            }] } });
const attrBoolValue$1 = (s) => s === '' || !!s;

/**
 * Helper directive to properly handle router events with markant-breadcrumb.
 *
 * Syncs an (if given) `routerLink` directive with the `href` property to
 * ensure native click behaviour (open in new tab, ...).
 *
 * Mainly derived from
 * https://github.com/angular/angular/blob/2c269c390a15b04419e176674a8af46e0ce29907/packages/router/src/directives/router_link.ts.
 */
class MarkantBreadcrumbDirective {
    constructor(router, locationStrategy, routerLinkDirective) {
        this.router = router;
        this.locationStrategy = locationStrategy;
        this.routerLinkDirective = routerLinkDirective;
        this.subscription = router.events.subscribe((s) => {
            if (s instanceof NavigationEnd) {
                this.maybeUpdateUrl();
            }
        });
    }
    onClick(button, ctrlKey, shiftKey, altKey, metaKey) {
        if (button !== 0 || ctrlKey || shiftKey || altKey || metaKey) {
            return true;
        }
        if (typeof this.target === 'string' && this.target !== '_self') {
            return true;
        }
        if (typeof this.download === 'string') {
            return true;
        }
        if (this.routerLinkDirective === null) {
            return true;
        }
        const { skipLocationChange, replaceUrl, urlTree, state } = this.routerLinkDirective;
        this.router.navigateByUrl(urlTree, {
            skipLocationChange: attrBoolValue(skipLocationChange),
            replaceUrl: attrBoolValue(replaceUrl),
            state: state,
        });
        return false;
    }
    ngOnChanges() {
        this.maybeUpdateUrl();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    maybeUpdateUrl() {
        if (this.routerLinkDirective !== null) {
            const urlTree = this.routerLinkDirective.urlTree;
            this.href =
                urlTree !== null ? this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(urlTree)) : null;
        }
    }
}
/** @nocollapse */ MarkantBreadcrumbDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantBreadcrumbDirective, deps: [{ token: i1.Router }, { token: i2.LocationStrategy }, { token: i1.RouterLink, host: true, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ MarkantBreadcrumbDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.10", type: MarkantBreadcrumbDirective, selector: "markant-breadcrumb", inputs: { target: "target", href: "href", download: "download" }, host: { listeners: { "click": "onClick($event.button,$event.ctrlKey,$event.shiftKey,$event.altKey,$event.metaKey)" }, properties: { "attr.target": "this.target", "attr.href": "this.href", "attr.download": "this.download" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantBreadcrumbDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'markant-breadcrumb',
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2.LocationStrategy }, { type: i1.RouterLink, decorators: [{
                    type: Host
                }, {
                    type: Optional
                }] }]; }, propDecorators: { target: [{
                type: Optional
            }, {
                type: HostBinding,
                args: ['attr.target']
            }, {
                type: Input
            }], href: [{
                type: Optional
            }, {
                type: HostBinding,
                args: ['attr.href']
            }, {
                type: Input
            }], download: [{
                type: Optional
            }, {
                type: HostBinding,
                args: ['attr.download']
            }, {
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event.button', '$event.ctrlKey', '$event.shiftKey', '$event.altKey', '$event.metaKey']]
            }] } });
const attrBoolValue = (s) => s === '' || !!s;

/* eslint-disable */
const proxyInputs = (Cmp, inputs) => {
    const Prototype = Cmp.prototype;
    inputs.forEach((item) => {
        Object.defineProperty(Prototype, item, {
            get() {
                return this.el[item];
            },
            set(val) {
                this.z.runOutsideAngular(() => (this.el[item] = val));
            },
        });
    });
};
const proxyMethods = (Cmp, methods) => {
    const Prototype = Cmp.prototype;
    methods.forEach((methodName) => {
        Prototype[methodName] = function () {
            const args = arguments;
            return this.z.runOutsideAngular(() => this.el[methodName].apply(this.el, args));
        };
    });
};
const proxyOutputs = (instance, el, events) => {
    events.forEach((eventName) => (instance[eventName] = fromEvent(el, eventName)));
};
const defineCustomElement = (tagName, customElement) => {
    if (customElement !== undefined && typeof customElements !== 'undefined' && !customElements.get(tagName)) {
        customElements.define(tagName, customElement);
    }
};
// tslint:disable-next-line: only-arrow-functions
function ProxyCmp(opts) {
    const decorator = function (cls) {
        const { defineCustomElementFn, inputs, methods } = opts;
        if (defineCustomElementFn !== undefined) {
            defineCustomElementFn();
        }
        if (inputs) {
            proxyInputs(cls, inputs);
        }
        if (methods) {
            proxyMethods(cls, methods);
        }
        return cls;
    };
    return decorator;
}

let MarkantBreadcrumb = class MarkantBreadcrumb {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['markantFocus', 'markantBlur']);
    }
};
/** @nocollapse */ MarkantBreadcrumb.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantBreadcrumb, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantBreadcrumb.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantBreadcrumb, selector: "markant-breadcrumb", inputs: { active: "active", disabled: "disabled", download: "download", href: "href", target: "target" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantBreadcrumb = __decorate([
    ProxyCmp({
        inputs: ['active', 'disabled', 'download', 'href', 'target']
    })
], MarkantBreadcrumb);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantBreadcrumb, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-breadcrumb',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['active', 'disabled', 'download', 'href', 'target'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantBreadcrumbs = class MarkantBreadcrumbs {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['markantCollapsedClicked']);
    }
};
/** @nocollapse */ MarkantBreadcrumbs.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantBreadcrumbs, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantBreadcrumbs.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantBreadcrumbs, selector: "markant-breadcrumbs", inputs: { autoCollapse: "autoCollapse", divider: "divider", itemsAfterCollapse: "itemsAfterCollapse", itemsBeforeCollapse: "itemsBeforeCollapse", maxItems: "maxItems", resizeDebounce: "resizeDebounce" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantBreadcrumbs = __decorate([
    ProxyCmp({
        inputs: ['autoCollapse', 'divider', 'itemsAfterCollapse', 'itemsBeforeCollapse', 'maxItems', 'resizeDebounce']
    })
], MarkantBreadcrumbs);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantBreadcrumbs, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-breadcrumbs',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoCollapse', 'divider', 'itemsAfterCollapse', 'itemsBeforeCollapse', 'maxItems', 'resizeDebounce'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantButton = class MarkantButton {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantButton, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantButton, selector: "markant-button", inputs: { autoFocus: "autoFocus", colorScheme: "colorScheme", dense: "dense", disabled: "disabled", form: "form", loading: "loading", name: "name", type: "type", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantButton = __decorate([
    ProxyCmp({
        inputs: ['autoFocus', 'colorScheme', 'dense', 'disabled', 'form', 'loading', 'name', 'type', 'variant']
    })
], MarkantButton);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantButton, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-button',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoFocus', 'colorScheme', 'dense', 'disabled', 'form', 'loading', 'name', 'type', 'variant'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCallout = class MarkantCallout {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantCallout.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCallout, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCallout.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCallout, selector: "markant-callout", inputs: { colorScheme: "colorScheme", removeable: "removeable", theme: "theme" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCallout = __decorate([
    ProxyCmp({
        inputs: ['colorScheme', 'removeable', 'theme']
    })
], MarkantCallout);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCallout, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-callout',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorScheme', 'removeable', 'theme'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCard = class MarkantCard {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantCard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCard, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCard.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCard, selector: "markant-card", inputs: { active: "active", hoverEffect: "hoverEffect", outlined: "outlined" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCard = __decorate([
    ProxyCmp({
        inputs: ['active', 'hoverEffect', 'outlined']
    })
], MarkantCard);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCard, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-card',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['active', 'hoverEffect', 'outlined'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCheckbox = class MarkantCheckbox {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['checkedChange', 'changed']);
    }
};
/** @nocollapse */ MarkantCheckbox.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCheckbox, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCheckbox.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCheckbox, selector: "markant-checkbox", inputs: { alignment: "alignment", checked: "checked", color: "color", disabled: "disabled", error: "error", indeterminate: "indeterminate", name: "name", required: "required", small: "small", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCheckbox = __decorate([
    ProxyCmp({
        inputs: ['alignment', 'checked', 'color', 'disabled', 'error', 'indeterminate', 'name', 'required', 'small', 'value']
    })
], MarkantCheckbox);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCheckbox, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-checkbox',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['alignment', 'checked', 'color', 'disabled', 'error', 'indeterminate', 'name', 'required', 'small', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCheckboxGroup = class MarkantCheckboxGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChanged']);
    }
};
/** @nocollapse */ MarkantCheckboxGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCheckboxGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCheckboxGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCheckboxGroup, selector: "markant-checkbox-group", inputs: { color: "color", error: "error", errorText: "errorText", legend: "legend", orientation: "orientation", required: "required", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCheckboxGroup = __decorate([
    ProxyCmp({
        inputs: ['color', 'error', 'errorText', 'legend', 'orientation', 'required', 'value']
    })
], MarkantCheckboxGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCheckboxGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-checkbox-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['color', 'error', 'errorText', 'legend', 'orientation', 'required', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantChip = class MarkantChip {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['removeChip', 'clickEl']);
    }
};
/** @nocollapse */ MarkantChip.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantChip, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantChip.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantChip, selector: "markant-chip", inputs: { colorScheme: "colorScheme", disabled: "disabled", fill: "fill", label: "label", readonly: "readonly", removable: "removable", selectable: "selectable", selected: "selected", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantChip = __decorate([
    ProxyCmp({
        inputs: ['colorScheme', 'disabled', 'fill', 'label', 'readonly', 'removable', 'selectable', 'selected', 'value']
    })
], MarkantChip);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantChip, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-chip',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorScheme', 'disabled', 'fill', 'label', 'readonly', 'removable', 'selectable', 'selected', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantColorPalette = class MarkantColorPalette {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantColorPalette.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantColorPalette, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantColorPalette.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantColorPalette, selector: "markant-color-palette", inputs: { borderStyle: "borderStyle", firstColor: "firstColor", firstColorCode: "firstColorCode", secondColor: "secondColor", secondColorCode: "secondColorCode", textColor: "textColor", thirdColor: "thirdColor", thirdColorCode: "thirdColorCode" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantColorPalette = __decorate([
    ProxyCmp({
        inputs: ['borderStyle', 'firstColor', 'firstColorCode', 'secondColor', 'secondColorCode', 'textColor', 'thirdColor', 'thirdColorCode']
    })
], MarkantColorPalette);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantColorPalette, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-color-palette',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['borderStyle', 'firstColor', 'firstColorCode', 'secondColor', 'secondColorCode', 'textColor', 'thirdColor', 'thirdColorCode'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCombiSelect = class MarkantCombiSelect {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'clear', 'optionSelectedChange', 'markantBlur', 'markantFocus']);
    }
};
/** @nocollapse */ MarkantCombiSelect.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelect, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCombiSelect.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCombiSelect, selector: "markant-combi-select", inputs: { autoFocus: "autoFocus", autocomplete: "autocomplete", clearable: "clearable", disabled: "disabled", error: "error", errorText: "errorText", helper: "helper", iconLeading: "iconLeading", isLoading: "isLoading", label: "label", name: "name", outlined: "outlined", placeholder: "placeholder", readonly: "readonly", required: "required", showLabelHint: "showLabelHint", type: "type", unit: "unit", useCustomLayout: "useCustomLayout", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCombiSelect = __decorate([
    ProxyCmp({
        inputs: ['autoFocus', 'autocomplete', 'clearable', 'disabled', 'error', 'errorText', 'helper', 'iconLeading', 'isLoading', 'label', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'showLabelHint', 'type', 'unit', 'useCustomLayout', 'value'],
        methods: ['getInputElement', 'setFocus', 'setBlur']
    })
], MarkantCombiSelect);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelect, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-combi-select',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoFocus', 'autocomplete', 'clearable', 'disabled', 'error', 'errorText', 'helper', 'iconLeading', 'isLoading', 'label', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'showLabelHint', 'type', 'unit', 'useCustomLayout', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCombiSelectNew = class MarkantCombiSelectNew {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'selectOption', 'markantBlur', 'markantFocus', 'clear']);
    }
};
/** @nocollapse */ MarkantCombiSelectNew.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelectNew, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCombiSelectNew.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCombiSelectNew, selector: "markant-combi-select-new", inputs: { autocomplete: "autocomplete", clearable: "clearable", disabled: "disabled", error: "error", helper: "helper", iconLeading: "iconLeading", label: "label", loading: "loading", meta: "meta", name: "name", outlined: "outlined", placeholder: "placeholder", readonly: "readonly", required: "required", requiredMessage: "requiredMessage", showLabelHint: "showLabelHint", truncateOptions: "truncateOptions", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCombiSelectNew = __decorate([
    ProxyCmp({
        inputs: ['autocomplete', 'clearable', 'disabled', 'error', 'helper', 'iconLeading', 'label', 'loading', 'meta', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'requiredMessage', 'showLabelHint', 'truncateOptions', 'value']
    })
], MarkantCombiSelectNew);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelectNew, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-combi-select-new',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autocomplete', 'clearable', 'disabled', 'error', 'helper', 'iconLeading', 'label', 'loading', 'meta', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'requiredMessage', 'showLabelHint', 'truncateOptions', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCombiSelectOption = class MarkantCombiSelectOption {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['selectedChange']);
    }
};
/** @nocollapse */ MarkantCombiSelectOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelectOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCombiSelectOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCombiSelectOption, selector: "markant-combi-select-option", inputs: { ellipse: "ellipse", hoverText: "hoverText", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCombiSelectOption = __decorate([
    ProxyCmp({
        inputs: ['ellipse', 'hoverText', 'value']
    })
], MarkantCombiSelectOption);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelectOption, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-combi-select-option',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['ellipse', 'hoverText', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCombiSelectOptionGroup = class MarkantCombiSelectOptionGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantCombiSelectOptionGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelectOptionGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCombiSelectOptionGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCombiSelectOptionGroup, selector: "markant-combi-select-option-group", inputs: { label: "label" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCombiSelectOptionGroup = __decorate([
    ProxyCmp({
        inputs: ['label']
    })
], MarkantCombiSelectOptionGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCombiSelectOptionGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-combi-select-option-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['label'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantCurrencyInput = class MarkantCurrencyInput {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange']);
    }
};
/** @nocollapse */ MarkantCurrencyInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCurrencyInput, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantCurrencyInput.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantCurrencyInput, selector: "markant-currency-input", inputs: { currencyLocale: "currencyLocale", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantCurrencyInput = __decorate([
    ProxyCmp({
        inputs: ['currencyLocale', 'value']
    })
], MarkantCurrencyInput);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantCurrencyInput, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-currency-input',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['currencyLocale', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantDatepicker = class MarkantDatepicker {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'validChange', 'dateChangeUTC', 'markantBlur', 'markantFocus']);
    }
};
/** @nocollapse */ MarkantDatepicker.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantDatepicker, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantDatepicker.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantDatepicker, selector: "markant-datepicker", inputs: { appendTo: "appendTo", autoFocus: "autoFocus", dateFormat: "dateFormat", defaultHour: "defaultHour", defaultMinute: "defaultMinute", disabled: "disabled", error: "error", errorText: "errorText", helper: "helper", hourStep: "hourStep", inline: "inline", label: "label", max: "max", min: "min", minuteStep: "minuteStep", name: "name", outlined: "outlined", placeholder: "placeholder", range: "range", readonly: "readonly", required: "required", showLabelHint: "showLabelHint", twelveHourTime: "twelveHourTime", type: "type", useCustomLayout: "useCustomLayout", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantDatepicker = __decorate([
    ProxyCmp({
        inputs: ['appendTo', 'autoFocus', 'dateFormat', 'defaultHour', 'defaultMinute', 'disabled', 'error', 'errorText', 'helper', 'hourStep', 'inline', 'label', 'max', 'min', 'minuteStep', 'name', 'outlined', 'placeholder', 'range', 'readonly', 'required', 'showLabelHint', 'twelveHourTime', 'type', 'useCustomLayout', 'value'],
        methods: ['redraw', 'setFocus', 'setBlur']
    })
], MarkantDatepicker);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantDatepicker, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-datepicker',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['appendTo', 'autoFocus', 'dateFormat', 'defaultHour', 'defaultMinute', 'disabled', 'error', 'errorText', 'helper', 'hourStep', 'inline', 'label', 'max', 'min', 'minuteStep', 'name', 'outlined', 'placeholder', 'range', 'readonly', 'required', 'showLabelHint', 'twelveHourTime', 'type', 'useCustomLayout', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantDialog = class MarkantDialog {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['close']);
    }
};
/** @nocollapse */ MarkantDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantDialog, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantDialog.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantDialog, selector: "markant-dialog", inputs: { critical: "critical", fullwidth: "fullwidth", headTitle: "headTitle", open: "open", preventCloseEscapeKey: "preventCloseEscapeKey", scrollLock: "scrollLock", subTitle: "subTitle" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantDialog = __decorate([
    ProxyCmp({
        inputs: ['critical', 'fullwidth', 'headTitle', 'open', 'preventCloseEscapeKey', 'scrollLock', 'subTitle']
    })
], MarkantDialog);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantDialog, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-dialog',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['critical', 'fullwidth', 'headTitle', 'open', 'preventCloseEscapeKey', 'scrollLock', 'subTitle'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantDropdownGroup = class MarkantDropdownGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantDropdownGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantDropdownGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantDropdownGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantDropdownGroup, selector: "markant-dropdown-group", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantDropdownGroup = __decorate([
    ProxyCmp({})
], MarkantDropdownGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantDropdownGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-dropdown-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: [],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantExpansionPanel = class MarkantExpansionPanel {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantExpansionPanel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantExpansionPanel, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantExpansionPanel.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantExpansionPanel, selector: "markant-expansion-panel", inputs: { multi: "multi", name: "name", showIndicator: "showIndicator" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantExpansionPanel = __decorate([
    ProxyCmp({
        inputs: ['multi', 'name', 'showIndicator'],
        methods: ['closeAll', 'openAll', 'openItem', 'closeItem']
    })
], MarkantExpansionPanel);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantExpansionPanel, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-expansion-panel',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['multi', 'name', 'showIndicator'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantExpansionPanelItem = class MarkantExpansionPanelItem {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['itemExpandedChange']);
    }
};
/** @nocollapse */ MarkantExpansionPanelItem.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantExpansionPanelItem, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantExpansionPanelItem.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantExpansionPanelItem, selector: "markant-expansion-panel-item", inputs: { colorMode: "colorMode", expandIconPosition: "expandIconPosition", expanded: "expanded", shouldAdjustHeight: "shouldAdjustHeight", showExpandIcon: "showExpandIcon", size: "size", truncateContent: "truncateContent", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantExpansionPanelItem = __decorate([
    ProxyCmp({
        inputs: ['colorMode', 'expandIconPosition', 'expanded', 'shouldAdjustHeight', 'showExpandIcon', 'size', 'truncateContent', 'variant']
    })
], MarkantExpansionPanelItem);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantExpansionPanelItem, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-expansion-panel-item',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorMode', 'expandIconPosition', 'expanded', 'shouldAdjustHeight', 'showExpandIcon', 'size', 'truncateContent', 'variant'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantFilterGroup = class MarkantFilterGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['filter']);
    }
};
/** @nocollapse */ MarkantFilterGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFilterGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantFilterGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantFilterGroup, selector: "markant-filter-group", inputs: { clearText: "clearText", excludeSearchbar: "excludeSearchbar", header: "header" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantFilterGroup = __decorate([
    ProxyCmp({
        inputs: ['clearText', 'excludeSearchbar', 'header'],
        methods: ['updateActiveFilters', 'getSearchbarInstance', 'clearActiveFilters', 'removeActiveFilter', 'getActiveFilters', 'registerMergeFn']
    })
], MarkantFilterGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFilterGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-filter-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['clearText', 'excludeSearchbar', 'header'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantFilterSelect = class MarkantFilterSelect {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['filterChange', 'searchbarValueChange']);
    }
};
/** @nocollapse */ MarkantFilterSelect.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFilterSelect, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantFilterSelect.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantFilterSelect, selector: "markant-filter-select", inputs: { appliedFilters: "appliedFilters", apply: "apply", disabled: "disabled", name: "name", options: "options", representation: "representation", reset: "reset", searchbar: "searchbar", searchbarPlaceholder: "searchbarPlaceholder" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantFilterSelect = __decorate([
    ProxyCmp({
        inputs: ['appliedFilters', 'apply', 'disabled', 'name', 'options', 'representation', 'reset', 'searchbar', 'searchbarPlaceholder'],
        methods: ['removeFilter', 'setFilters']
    })
], MarkantFilterSelect);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFilterSelect, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-filter-select',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['appliedFilters', 'apply', 'disabled', 'name', 'options', 'representation', 'reset', 'searchbar', 'searchbarPlaceholder'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantFilterSelectOption = class MarkantFilterSelectOption {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantFilterSelectOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFilterSelectOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantFilterSelectOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantFilterSelectOption, selector: "markant-filter-select-option", inputs: { value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantFilterSelectOption = __decorate([
    ProxyCmp({
        inputs: ['value']
    })
], MarkantFilterSelectOption);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFilterSelectOption, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-filter-select-option',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantFooter = class MarkantFooter {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantFooter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFooter, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantFooter.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantFooter, selector: "markant-footer", inputs: { invert: "invert" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantFooter = __decorate([
    ProxyCmp({
        inputs: ['invert']
    })
], MarkantFooter);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantFooter, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-footer',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['invert'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantGuideArrow = class MarkantGuideArrow {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantGuideArrow.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantGuideArrow, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantGuideArrow.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantGuideArrow, selector: "markant-guide-arrow", inputs: { animationTime: "animationTime", arrowAttachesTo: "arrowAttachesTo", fromX: "fromX", fromY: "fromY", toX: "toX", toY: "toY" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantGuideArrow = __decorate([
    ProxyCmp({
        inputs: ['animationTime', 'arrowAttachesTo', 'fromX', 'fromY', 'toX', 'toY']
    })
], MarkantGuideArrow);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantGuideArrow, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-guide-arrow',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['animationTime', 'arrowAttachesTo', 'fromX', 'fromY', 'toX', 'toY'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantGuidedTour = class MarkantGuidedTour {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['changed']);
    }
};
/** @nocollapse */ MarkantGuidedTour.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantGuidedTour, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantGuidedTour.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantGuidedTour, selector: "markant-guided-tour", inputs: { arrowAttachment: "arrowAttachment", backButton: "backButton", blurred: "blurred", border: "border", buttonAlignment: "buttonAlignment", buttonPosition: "buttonPosition", darkened: "darkened", enableClickThrough: "enableClickThrough", enableNextOnClick: "enableNextOnClick", hideArrow: "hideArrow", nextButton: "nextButton", shape: "shape", showStatus: "showStatus", starterId: "starterId", target: "target" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantGuidedTour = __decorate([
    ProxyCmp({
        inputs: ['arrowAttachment', 'backButton', 'blurred', 'border', 'buttonAlignment', 'buttonPosition', 'darkened', 'enableClickThrough', 'enableNextOnClick', 'hideArrow', 'nextButton', 'shape', 'showStatus', 'starterId', 'target'],
        methods: ['start', 'next', 'jumpTo', 'back', 'end']
    })
], MarkantGuidedTour);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantGuidedTour, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-guided-tour',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['arrowAttachment', 'backButton', 'blurred', 'border', 'buttonAlignment', 'buttonPosition', 'darkened', 'enableClickThrough', 'enableNextOnClick', 'hideArrow', 'nextButton', 'shape', 'showStatus', 'starterId', 'target'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantHeader = class MarkantHeader {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantHeader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantHeader, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantHeader.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantHeader, selector: "markant-header", inputs: { dashboardIcon: "dashboardIcon" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantHeader = __decorate([
    ProxyCmp({
        inputs: ['dashboardIcon']
    })
], MarkantHeader);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantHeader, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-header',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['dashboardIcon'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantHeaderLogoBar = class MarkantHeaderLogoBar {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantHeaderLogoBar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantHeaderLogoBar, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantHeaderLogoBar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantHeaderLogoBar, selector: "markant-header-logo-bar", inputs: { logoBarTitle: "logoBarTitle", logoUrl: "logoUrl" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantHeaderLogoBar = __decorate([
    ProxyCmp({
        inputs: ['logoBarTitle', 'logoUrl']
    })
], MarkantHeaderLogoBar);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantHeaderLogoBar, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-header-logo-bar',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['logoBarTitle', 'logoUrl'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantHighlight = class MarkantHighlight {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['close', 'next', 'back']);
    }
};
/** @nocollapse */ MarkantHighlight.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantHighlight, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantHighlight.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantHighlight, selector: "markant-highlight", inputs: { active: "active", arrowAttachment: "arrowAttachment", blurred: "blurred", border: "border", darkened: "darkened", description: "description", disableClickThrough: "disableClickThrough", hideArrow: "hideArrow", nextOnClick: "nextOnClick", shape: "shape", target: "target" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantHighlight = __decorate([
    ProxyCmp({
        inputs: ['active', 'arrowAttachment', 'blurred', 'border', 'darkened', 'description', 'disableClickThrough', 'hideArrow', 'nextOnClick', 'shape', 'target'],
        methods: ['refresh']
    })
], MarkantHighlight);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantHighlight, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-highlight',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['active', 'arrowAttachment', 'blurred', 'border', 'darkened', 'description', 'disableClickThrough', 'hideArrow', 'nextOnClick', 'shape', 'target'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantIcon = class MarkantIcon {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['clickEl']);
    }
};
/** @nocollapse */ MarkantIcon.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantIcon, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantIcon.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantIcon, selector: "markant-icon", inputs: { clickable: "clickable", colorScheme: "colorScheme", icon: "icon", src: "src", svgTitle: "svgTitle" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantIcon = __decorate([
    ProxyCmp({
        inputs: ['clickable', 'colorScheme', 'icon', 'src', 'svgTitle']
    })
], MarkantIcon);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantIcon, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-icon',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['clickable', 'colorScheme', 'icon', 'src', 'svgTitle'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantIconButton = class MarkantIconButton {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['clickEl']);
    }
};
/** @nocollapse */ MarkantIconButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantIconButton, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantIconButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantIconButton, selector: "markant-icon-button", inputs: { activated: "activated", autoFocus: "autoFocus", colorScheme: "colorScheme", disabled: "disabled", filled: "filled", icon: "icon", type: "type" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantIconButton = __decorate([
    ProxyCmp({
        inputs: ['activated', 'autoFocus', 'colorScheme', 'disabled', 'filled', 'icon', 'type']
    })
], MarkantIconButton);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantIconButton, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-icon-button',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['activated', 'autoFocus', 'colorScheme', 'disabled', 'filled', 'icon', 'type'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantInput = class MarkantInput {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'markantBlur', 'markantFocus']);
    }
};
/** @nocollapse */ MarkantInput.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInput, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantInput.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantInput, selector: "markant-input", inputs: { autoFocus: "autoFocus", autocomplete: "autocomplete", dataList: "dataList", debounce: "debounce", disabled: "disabled", error: "error", errorText: "errorText", helper: "helper", helperCharacterCounter: "helperCharacterCounter", label: "label", max: "max", maxlength: "maxlength", min: "min", name: "name", outlined: "outlined", pattern: "pattern", placeholder: "placeholder", readonly: "readonly", required: "required", search: "search", showLabelHint: "showLabelHint", size: "size", step: "step", type: "type", unit: "unit", useCustomLayout: "useCustomLayout", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantInput = __decorate([
    ProxyCmp({
        inputs: ['autoFocus', 'autocomplete', 'dataList', 'debounce', 'disabled', 'error', 'errorText', 'helper', 'helperCharacterCounter', 'label', 'max', 'maxlength', 'min', 'name', 'outlined', 'pattern', 'placeholder', 'readonly', 'required', 'search', 'showLabelHint', 'size', 'step', 'type', 'unit', 'useCustomLayout', 'value'],
        methods: ['getInputElement', 'setFocus', 'setBlur']
    })
], MarkantInput);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInput, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-input',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoFocus', 'autocomplete', 'dataList', 'debounce', 'disabled', 'error', 'errorText', 'helper', 'helperCharacterCounter', 'label', 'max', 'maxlength', 'min', 'name', 'outlined', 'pattern', 'placeholder', 'readonly', 'required', 'search', 'showLabelHint', 'size', 'step', 'type', 'unit', 'useCustomLayout', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantInputDivided = class MarkantInputDivided {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange']);
    }
};
/** @nocollapse */ MarkantInputDivided.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputDivided, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantInputDivided.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantInputDivided, selector: "markant-input-divided", inputs: { autoFocus: "autoFocus", disabled: "disabled", error: "error", errorText: "errorText", helper: "helper", label: "label", outlined: "outlined", placeholder: "placeholder", readonly: "readonly", required: "required", selectOptions: "selectOptions", showLabelHint: "showLabelHint", type: "type", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantInputDivided = __decorate([
    ProxyCmp({
        inputs: ['autoFocus', 'disabled', 'error', 'errorText', 'helper', 'label', 'outlined', 'placeholder', 'readonly', 'required', 'selectOptions', 'showLabelHint', 'type', 'value']
    })
], MarkantInputDivided);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputDivided, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-input-divided',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoFocus', 'disabled', 'error', 'errorText', 'helper', 'label', 'outlined', 'placeholder', 'readonly', 'required', 'selectOptions', 'showLabelHint', 'type', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantInputDividedOption = class MarkantInputDividedOption {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['optionSelect']);
    }
};
/** @nocollapse */ MarkantInputDividedOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputDividedOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantInputDividedOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantInputDividedOption, selector: "markant-input-divided-option", inputs: { representation: "representation", selected: "selected", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantInputDividedOption = __decorate([
    ProxyCmp({
        inputs: ['representation', 'selected', 'value']
    })
], MarkantInputDividedOption);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputDividedOption, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-input-divided-option',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['representation', 'selected', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantInputFile = class MarkantInputFile {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['changeFile']);
    }
};
/** @nocollapse */ MarkantInputFile.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputFile, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantInputFile.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantInputFile, selector: "markant-input-file", inputs: { accept: "accept", allowedFilesPrefixText: "allowedFilesPrefixText", autoFocus: "autoFocus", dense: "dense", disabled: "disabled", dragAndDrop: "dragAndDrop", dragAndDropHoverText: "dragAndDropHoverText", dragAndDropSecondaryText: "dragAndDropSecondaryText", dragAndDropText: "dragAndDropText", label: "label", multiple: "multiple", name: "name", required: "required", requiredText: "requiredText", uploading: "uploading" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantInputFile = __decorate([
    ProxyCmp({
        inputs: ['accept', 'allowedFilesPrefixText', 'autoFocus', 'dense', 'disabled', 'dragAndDrop', 'dragAndDropHoverText', 'dragAndDropSecondaryText', 'dragAndDropText', 'label', 'multiple', 'name', 'required', 'requiredText', 'uploading']
    })
], MarkantInputFile);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputFile, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-input-file',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['accept', 'allowedFilesPrefixText', 'autoFocus', 'dense', 'disabled', 'dragAndDrop', 'dragAndDropHoverText', 'dragAndDropSecondaryText', 'dragAndDropText', 'label', 'multiple', 'name', 'required', 'requiredText', 'uploading'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantInputFileProgressbar = class MarkantInputFileProgressbar {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['removeFile', 'reuploadFile']);
    }
};
/** @nocollapse */ MarkantInputFileProgressbar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputFileProgressbar, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantInputFileProgressbar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantInputFileProgressbar, selector: "markant-input-file-progressbar", inputs: { allowReuploads: "allowReuploads", buffer: "buffer", errorTooltip: "errorTooltip", fileName: "fileName", fileSize: "fileSize", indeterminate: "indeterminate", progress: "progress", refreshTooltip: "refreshTooltip", status: "status" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantInputFileProgressbar = __decorate([
    ProxyCmp({
        inputs: ['allowReuploads', 'buffer', 'errorTooltip', 'fileName', 'fileSize', 'indeterminate', 'progress', 'refreshTooltip', 'status']
    })
], MarkantInputFileProgressbar);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputFileProgressbar, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-input-file-progressbar',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['allowReuploads', 'buffer', 'errorTooltip', 'fileName', 'fileSize', 'indeterminate', 'progress', 'refreshTooltip', 'status'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantInputNew = class MarkantInputNew {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'markantBlur', 'markantFocus']);
    }
};
/** @nocollapse */ MarkantInputNew.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputNew, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantInputNew.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantInputNew, selector: "markant-input-new", inputs: { autoFocus: "autoFocus", autocomplete: "autocomplete", debounce: "debounce", defaultInvalidMessage: "defaultInvalidMessage", disabled: "disabled", error: "error", helper: "helper", helperCharacterCounter: "helperCharacterCounter", label: "label", max: "max", maxlength: "maxlength", meta: "meta", min: "min", name: "name", outlined: "outlined", pattern: "pattern", placeholder: "placeholder", readonly: "readonly", required: "required", showLabelHint: "showLabelHint", step: "step", type: "type", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantInputNew = __decorate([
    ProxyCmp({
        inputs: ['autoFocus', 'autocomplete', 'debounce', 'defaultInvalidMessage', 'disabled', 'error', 'helper', 'helperCharacterCounter', 'label', 'max', 'maxlength', 'meta', 'min', 'name', 'outlined', 'pattern', 'placeholder', 'readonly', 'required', 'showLabelHint', 'step', 'type', 'value'],
        methods: ['setFocus', 'setBlur']
    })
], MarkantInputNew);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputNew, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-input-new',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoFocus', 'autocomplete', 'debounce', 'defaultInvalidMessage', 'disabled', 'error', 'helper', 'helperCharacterCounter', 'label', 'max', 'maxlength', 'meta', 'min', 'name', 'outlined', 'pattern', 'placeholder', 'readonly', 'required', 'showLabelHint', 'step', 'type', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantInputNumber = class MarkantInputNumber {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'markantBlur', 'markantFocus']);
    }
};
/** @nocollapse */ MarkantInputNumber.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputNumber, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantInputNumber.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantInputNumber, selector: "markant-input-number", inputs: { autoFocus: "autoFocus", disabled: "disabled", error: "error", formatOptions: "formatOptions", helper: "helper", helperPersistent: "helperPersistent", helperValidation: "helperValidation", initialValue: "initialValue", label: "label", locale: "locale", max: "max", min: "min", name: "name", outlined: "outlined", placeholder: "placeholder", readonly: "readonly", required: "required", showLabelHint: "showLabelHint", step: "step", useCustomLayout: "useCustomLayout" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantInputNumber = __decorate([
    ProxyCmp({
        inputs: ['autoFocus', 'disabled', 'error', 'formatOptions', 'helper', 'helperPersistent', 'helperValidation', 'initialValue', 'label', 'locale', 'max', 'min', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'showLabelHint', 'step', 'useCustomLayout'],
        methods: ['getInputElement', 'setFocus', 'setBlur']
    })
], MarkantInputNumber);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantInputNumber, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-input-number',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoFocus', 'disabled', 'error', 'formatOptions', 'helper', 'helperPersistent', 'helperValidation', 'initialValue', 'label', 'locale', 'max', 'min', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'showLabelHint', 'step', 'useCustomLayout'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantLabel = class MarkantLabel {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantLabel.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantLabel, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantLabel.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantLabel, selector: "markant-label", inputs: { disabled: "disabled", required: "required", showHint: "showHint", text: "text" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantLabel = __decorate([
    ProxyCmp({
        inputs: ['disabled', 'required', 'showHint', 'text']
    })
], MarkantLabel);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantLabel, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-label',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['disabled', 'required', 'showHint', 'text'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantLeistungsHeader = class MarkantLeistungsHeader {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantLeistungsHeader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantLeistungsHeader, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantLeistungsHeader.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantLeistungsHeader, selector: "markant-leistungs-header", inputs: { extendToLeft: "extendToLeft", icon: "icon", label: "label", linkTo: "linkTo", search: "search", target: "target" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantLeistungsHeader = __decorate([
    ProxyCmp({
        inputs: ['extendToLeft', 'icon', 'label', 'linkTo', 'search', 'target']
    })
], MarkantLeistungsHeader);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantLeistungsHeader, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-leistungs-header',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['extendToLeft', 'icon', 'label', 'linkTo', 'search', 'target'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantList = class MarkantList {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantList.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantList, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantList.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantList, selector: "markant-list", inputs: { dense: "dense", leadingAvatar: "leadingAvatar", twoLines: "twoLines" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantList = __decorate([
    ProxyCmp({
        inputs: ['dense', 'leadingAvatar', 'twoLines']
    })
], MarkantList);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantList, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-list',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['dense', 'leadingAvatar', 'twoLines'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantListDivider = class MarkantListDivider {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantListDivider.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantListDivider, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantListDivider.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantListDivider, selector: "markant-list-divider", inputs: { betweenLists: "betweenLists", inset: "inset", padded: "padded" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantListDivider = __decorate([
    ProxyCmp({
        inputs: ['betweenLists', 'inset', 'padded']
    })
], MarkantListDivider);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantListDivider, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-list-divider',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['betweenLists', 'inset', 'padded'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantListItem = class MarkantListItem {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['clickEl']);
    }
};
/** @nocollapse */ MarkantListItem.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantListItem, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantListItem.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantListItem, selector: "markant-list-item", inputs: { activated: "activated", disabled: "disabled", secondaryText: "secondaryText", selected: "selected", text: "text", truncateContent: "truncateContent" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantListItem = __decorate([
    ProxyCmp({
        inputs: ['activated', 'disabled', 'secondaryText', 'selected', 'text', 'truncateContent']
    })
], MarkantListItem);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantListItem, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-list-item',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['activated', 'disabled', 'secondaryText', 'selected', 'text', 'truncateContent'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantNavDrawer = class MarkantNavDrawer {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['openChange']);
    }
};
/** @nocollapse */ MarkantNavDrawer.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavDrawer, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantNavDrawer.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantNavDrawer, selector: "markant-nav-drawer", inputs: { anchor: "anchor", open: "open", toggleText: "toggleText", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantNavDrawer = __decorate([
    ProxyCmp({
        inputs: ['anchor', 'open', 'toggleText', 'variant']
    })
], MarkantNavDrawer);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavDrawer, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-nav-drawer',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['anchor', 'open', 'toggleText', 'variant'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantNavDrawerSubnav = class MarkantNavDrawerSubnav {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['openChange']);
    }
};
/** @nocollapse */ MarkantNavDrawerSubnav.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavDrawerSubnav, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantNavDrawerSubnav.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantNavDrawerSubnav, selector: "markant-nav-drawer-subnav", inputs: { anchor: "anchor", open: "open", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantNavDrawerSubnav = __decorate([
    ProxyCmp({
        inputs: ['anchor', 'open', 'variant']
    })
], MarkantNavDrawerSubnav);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavDrawerSubnav, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-nav-drawer-subnav',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['anchor', 'open', 'variant'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantNavItem = class MarkantNavItem {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['changeSubnav']);
    }
};
/** @nocollapse */ MarkantNavItem.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavItem, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantNavItem.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantNavItem, selector: "markant-nav-item", inputs: { active: "active", disabled: "disabled", extendToLeft: "extendToLeft", extended: "extended", icon: "icon", label: "label", linkTo: "linkTo", new: "new", target: "target" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantNavItem = __decorate([
    ProxyCmp({
        inputs: ['active', 'disabled', 'extendToLeft', 'extended', 'icon', 'label', 'linkTo', 'new', 'target']
    })
], MarkantNavItem);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNavItem, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-nav-item',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['active', 'disabled', 'extendToLeft', 'extended', 'icon', 'label', 'linkTo', 'new', 'target'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantNotificationBadge = class MarkantNotificationBadge {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantNotificationBadge.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNotificationBadge, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantNotificationBadge.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantNotificationBadge, selector: "markant-notification-badge", inputs: { colorScheme: "colorScheme", count: "count", maxCounter: "maxCounter", position: "position", pulse: "pulse", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantNotificationBadge = __decorate([
    ProxyCmp({
        inputs: ['colorScheme', 'count', 'maxCounter', 'position', 'pulse', 'size']
    })
], MarkantNotificationBadge);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantNotificationBadge, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-notification-badge',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorScheme', 'count', 'maxCounter', 'position', 'pulse', 'size'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantPage = class MarkantPage {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantPage.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPage, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantPage.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantPage, selector: "markant-page", ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantPage = __decorate([
    ProxyCmp({})
], MarkantPage);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPage, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-page',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: [],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantPageHeader = class MarkantPageHeader {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantPageHeader.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPageHeader, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantPageHeader.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantPageHeader, selector: "markant-page-header", inputs: { closable: "closable" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantPageHeader = __decorate([
    ProxyCmp({
        inputs: ['closable']
    })
], MarkantPageHeader);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPageHeader, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-page-header',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['closable'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantPageOverlay = class MarkantPageOverlay {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['close']);
    }
};
/** @nocollapse */ MarkantPageOverlay.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPageOverlay, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantPageOverlay.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantPageOverlay, selector: "markant-page-overlay", inputs: { open: "open" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantPageOverlay = __decorate([
    ProxyCmp({
        inputs: ['open']
    })
], MarkantPageOverlay);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPageOverlay, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-page-overlay',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['open'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantPagination = class MarkantPagination {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['pageSizeChange', 'indexChange']);
    }
};
/** @nocollapse */ MarkantPagination.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPagination, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantPagination.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantPagination, selector: "markant-pagination", inputs: { goToLabel: "goToLabel", index: "index", managed: "managed", pageSize: "pageSize", selectLabel: "selectLabel", showGoTo: "showGoTo", totalCount: "totalCount" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantPagination = __decorate([
    ProxyCmp({
        inputs: ['goToLabel', 'index', 'managed', 'pageSize', 'selectLabel', 'showGoTo', 'totalCount']
    })
], MarkantPagination);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPagination, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-pagination',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['goToLabel', 'index', 'managed', 'pageSize', 'selectLabel', 'showGoTo', 'totalCount'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantPaginationSelectOption = class MarkantPaginationSelectOption {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantPaginationSelectOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPaginationSelectOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantPaginationSelectOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantPaginationSelectOption, selector: "markant-pagination-select-option", inputs: { disabled: "disabled", selected: "selected", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantPaginationSelectOption = __decorate([
    ProxyCmp({
        inputs: ['disabled', 'selected', 'value']
    })
], MarkantPaginationSelectOption);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPaginationSelectOption, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-pagination-select-option',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['disabled', 'selected', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantPopover = class MarkantPopover {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['backdrop', 'visibleChange', 'teleport', 'visibleChangeTrigger', 'placementChange']);
    }
};
/** @nocollapse */ MarkantPopover.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPopover, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantPopover.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantPopover, selector: "markant-popover", inputs: { arrow: "arrow", colorScheme: "colorScheme", distance: "distance", fixedPlacement: "fixedPlacement", for: "for", hideTrigger: "hideTrigger", placement: "placement", shift: "shift", trigger: "trigger", triggerTarget: "triggerTarget", visible: "visible" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantPopover = __decorate([
    ProxyCmp({
        inputs: ['arrow', 'colorScheme', 'distance', 'fixedPlacement', 'for', 'hideTrigger', 'placement', 'shift', 'trigger', 'triggerTarget', 'visible']
    })
], MarkantPopover);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantPopover, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-popover',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['arrow', 'colorScheme', 'distance', 'fixedPlacement', 'for', 'hideTrigger', 'placement', 'shift', 'trigger', 'triggerTarget', 'visible'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantProgressBar = class MarkantProgressBar {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantProgressBar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantProgressBar, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantProgressBar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantProgressBar, selector: "markant-progress-bar", inputs: { buffer: "buffer", debounce: "debounce", inactive: "inactive", indeterminate: "indeterminate", label: "label", progress: "progress" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantProgressBar = __decorate([
    ProxyCmp({
        inputs: ['buffer', 'debounce', 'inactive', 'indeterminate', 'label', 'progress']
    })
], MarkantProgressBar);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantProgressBar, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-progress-bar',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['buffer', 'debounce', 'inactive', 'indeterminate', 'label', 'progress'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantRadio = class MarkantRadio {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['checkedChange']);
    }
};
/** @nocollapse */ MarkantRadio.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantRadio, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantRadio.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantRadio, selector: "markant-radio", inputs: { alignment: "alignment", checked: "checked", color: "color", disabled: "disabled", error: "error", name: "name", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantRadio = __decorate([
    ProxyCmp({
        inputs: ['alignment', 'checked', 'color', 'disabled', 'error', 'name', 'value']
    })
], MarkantRadio);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantRadio, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-radio',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['alignment', 'checked', 'color', 'disabled', 'error', 'name', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantRadioGroup = class MarkantRadioGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChanged']);
    }
};
/** @nocollapse */ MarkantRadioGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantRadioGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantRadioGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantRadioGroup, selector: "markant-radio-group", inputs: { color: "color", error: "error", errorText: "errorText", legend: "legend", orientation: "orientation", required: "required", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantRadioGroup = __decorate([
    ProxyCmp({
        inputs: ['color', 'error', 'errorText', 'legend', 'orientation', 'required', 'value']
    })
], MarkantRadioGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantRadioGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-radio-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['color', 'error', 'errorText', 'legend', 'orientation', 'required', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantRange = class MarkantRange {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange']);
    }
};
/** @nocollapse */ MarkantRange.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantRange, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantRange.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantRange, selector: "markant-range", inputs: { colorScheme: "colorScheme", disabled: "disabled", discrete: "discrete", markers: "markers", max: "max", min: "min", name: "name", step: "step", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantRange = __decorate([
    ProxyCmp({
        inputs: ['colorScheme', 'disabled', 'discrete', 'markers', 'max', 'min', 'name', 'step', 'value']
    })
], MarkantRange);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantRange, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-range',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorScheme', 'disabled', 'discrete', 'markers', 'max', 'min', 'name', 'step', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSearchbar = class MarkantSearchbar {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['iconClick', 'search', 'valueChange', 'categoryChange']);
    }
};
/** @nocollapse */ MarkantSearchbar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSearchbar, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSearchbar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSearchbar, selector: "markant-searchbar", inputs: { categories: "categories", debounce: "debounce", dense: "dense", disabled: "disabled", icon: "icon", iconDisabled: "iconDisabled", label: "label", loading: "loading", outlined: "outlined", placeholder: "placeholder", suggestionInsertionEnabled: "suggestionInsertionEnabled", suggestions: "suggestions", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSearchbar = __decorate([
    ProxyCmp({
        inputs: ['categories', 'debounce', 'dense', 'disabled', 'icon', 'iconDisabled', 'label', 'loading', 'outlined', 'placeholder', 'suggestionInsertionEnabled', 'suggestions', 'value'],
        methods: ['openSuggestionMenu', 'closeSuggestionMenu', 'triggerSearch', 'getSuggestionMenuInstance']
    })
], MarkantSearchbar);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSearchbar, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-searchbar',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['categories', 'debounce', 'dense', 'disabled', 'icon', 'iconDisabled', 'label', 'loading', 'outlined', 'placeholder', 'suggestionInsertionEnabled', 'suggestions', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSearchbarCategory = class MarkantSearchbarCategory {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['categorySelect', 'focusNextCategory', 'focusPrevCategory', 'closeCategories']);
    }
};
/** @nocollapse */ MarkantSearchbarCategory.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSearchbarCategory, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSearchbarCategory.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSearchbarCategory, selector: "markant-searchbar-category", inputs: { representation: "representation", selected: "selected", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSearchbarCategory = __decorate([
    ProxyCmp({
        inputs: ['representation', 'selected', 'value']
    })
], MarkantSearchbarCategory);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSearchbarCategory, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-searchbar-category',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['representation', 'selected', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSearchbarSuggestion = class MarkantSearchbarSuggestion {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['insert', 'focusNextSuggestion', 'focusPrevSuggestion', 'closeSuggestions', 'searchSuggestion']);
    }
};
/** @nocollapse */ MarkantSearchbarSuggestion.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSearchbarSuggestion, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSearchbarSuggestion.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSearchbarSuggestion, selector: "markant-searchbar-suggestion", inputs: { insertionEnabled: "insertionEnabled", text: "text" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSearchbarSuggestion = __decorate([
    ProxyCmp({
        inputs: ['insertionEnabled', 'text']
    })
], MarkantSearchbarSuggestion);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSearchbarSuggestion, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-searchbar-suggestion',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['insertionEnabled', 'text'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSegmentedControl = class MarkantSegmentedControl {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['checkedChange', 'valueChange']);
    }
};
/** @nocollapse */ MarkantSegmentedControl.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSegmentedControl, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSegmentedControl.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSegmentedControl, selector: "markant-segmented-control", inputs: { checked: "checked", disabled: "disabled", icon: "icon", label: "label", name: "name", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSegmentedControl = __decorate([
    ProxyCmp({
        inputs: ['checked', 'disabled', 'icon', 'label', 'name', 'value']
    })
], MarkantSegmentedControl);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSegmentedControl, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-segmented-control',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['checked', 'disabled', 'icon', 'label', 'name', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSegmentedControlGroup = class MarkantSegmentedControlGroup {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange']);
    }
};
/** @nocollapse */ MarkantSegmentedControlGroup.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSegmentedControlGroup, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSegmentedControlGroup.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSegmentedControlGroup, selector: "markant-segmented-control-group", inputs: { colorScheme: "colorScheme", dense: "dense", disabled: "disabled", orientation: "orientation", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSegmentedControlGroup = __decorate([
    ProxyCmp({
        inputs: ['colorScheme', 'dense', 'disabled', 'orientation', 'value']
    })
], MarkantSegmentedControlGroup);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSegmentedControlGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-segmented-control-group',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorScheme', 'dense', 'disabled', 'orientation', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSelect = class MarkantSelect {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'markantBlur', 'iconClick']);
    }
};
/** @nocollapse */ MarkantSelect.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSelect, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSelect.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSelect, selector: "markant-select", inputs: { disabled: "disabled", error: "error", helper: "helper", icon: "icon", iconDisabled: "iconDisabled", label: "label", meta: "meta", multiselect: "multiselect", name: "name", options: "options", optionsFilter: "optionsFilter", outlined: "outlined", placeholder: "placeholder", readonly: "readonly", required: "required", requiredMessage: "requiredMessage", searchPlaceholder: "searchPlaceholder", searchable: "searchable", showLabelHint: "showLabelHint", truncateOptions: "truncateOptions", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSelect = __decorate([
    ProxyCmp({
        inputs: ['disabled', 'error', 'helper', 'icon', 'iconDisabled', 'label', 'meta', 'multiselect', 'name', 'options', 'optionsFilter', 'outlined', 'placeholder', 'readonly', 'required', 'requiredMessage', 'searchPlaceholder', 'searchable', 'showLabelHint', 'truncateOptions', 'value']
    })
], MarkantSelect);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSelect, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-select',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['disabled', 'error', 'helper', 'icon', 'iconDisabled', 'label', 'meta', 'multiselect', 'name', 'options', 'optionsFilter', 'outlined', 'placeholder', 'readonly', 'required', 'requiredMessage', 'searchPlaceholder', 'searchable', 'showLabelHint', 'truncateOptions', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSelectOption = class MarkantSelectOption {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['checkedChange', 'focusNext', 'focusPrev', 'closeMenu']);
    }
};
/** @nocollapse */ MarkantSelectOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSelectOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSelectOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSelectOption, selector: "markant-select-option", inputs: { checked: "checked", disabled: "disabled", multiselect: "multiselect", representation: "representation", truncate: "truncate", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSelectOption = __decorate([
    ProxyCmp({
        inputs: ['checked', 'disabled', 'multiselect', 'representation', 'truncate', 'value']
    })
], MarkantSelectOption);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSelectOption, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-select-option',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['checked', 'disabled', 'multiselect', 'representation', 'truncate', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSeperator = class MarkantSeperator {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSeperator.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSeperator, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSeperator.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSeperator, selector: "markant-seperator", inputs: { orientation: "orientation" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSeperator = __decorate([
    ProxyCmp({
        inputs: ['orientation']
    })
], MarkantSeperator);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSeperator, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-seperator',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['orientation'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSkeletonScreen = class MarkantSkeletonScreen {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSkeletonScreen.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSkeletonScreen, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSkeletonScreen.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSkeletonScreen, selector: "markant-skeleton-screen", inputs: { animation: "animation", autoAdjustment: "autoAdjustment", for: "for", height: "height", loading: "loading", width: "width" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSkeletonScreen = __decorate([
    ProxyCmp({
        inputs: ['animation', 'autoAdjustment', 'for', 'height', 'loading', 'width']
    })
], MarkantSkeletonScreen);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSkeletonScreen, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-skeleton-screen',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['animation', 'autoAdjustment', 'for', 'height', 'loading', 'width'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSlide = class MarkantSlide {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSlide.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSlide, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSlide.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSlide, selector: "markant-slide", inputs: { alt: "alt", src: "src" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSlide = __decorate([
    ProxyCmp({
        inputs: ['alt', 'src']
    })
], MarkantSlide);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSlide, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-slide',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['alt', 'src'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSlider = class MarkantSlider {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSlider.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSlider, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSlider.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSlider, selector: "markant-slider", inputs: { animation: "animation", blurLevel: "blurLevel", endless: "endless", indicator: "indicator", mode: "mode", navigation: "navigation", padding: "padding", size: "size", sliderInterval: "sliderInterval" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSlider = __decorate([
    ProxyCmp({
        inputs: ['animation', 'blurLevel', 'endless', 'indicator', 'mode', 'navigation', 'padding', 'size', 'sliderInterval']
    })
], MarkantSlider);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSlider, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-slider',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['animation', 'blurLevel', 'endless', 'indicator', 'mode', 'navigation', 'padding', 'size', 'sliderInterval'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSnackbar = class MarkantSnackbar {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['hidden']);
    }
};
/** @nocollapse */ MarkantSnackbar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSnackbar, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSnackbar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSnackbar, selector: "markant-snackbar", inputs: { heading: "heading", message: "message", showClose: "showClose", timeout: "timeout", type: "type" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSnackbar = __decorate([
    ProxyCmp({
        inputs: ['heading', 'message', 'showClose', 'timeout', 'type']
    })
], MarkantSnackbar);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSnackbar, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-snackbar',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['heading', 'message', 'showClose', 'timeout', 'type'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSpinner = class MarkantSpinner {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSpinner.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSpinner, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSpinner.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSpinner, selector: "markant-spinner", inputs: { colorScheme: "colorScheme", modal: "modal", type: "type" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSpinner = __decorate([
    ProxyCmp({
        inputs: ['colorScheme', 'modal', 'type']
    })
], MarkantSpinner);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSpinner, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-spinner',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorScheme', 'modal', 'type'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantStepIndicator = class MarkantStepIndicator {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantStepIndicator.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantStepIndicator, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantStepIndicator.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantStepIndicator, selector: "markant-step-indicator", inputs: { status: "status", step: "step" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantStepIndicator = __decorate([
    ProxyCmp({
        inputs: ['status', 'step']
    })
], MarkantStepIndicator);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantStepIndicator, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-step-indicator',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['status', 'step'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantStepperHorizontal = class MarkantStepperHorizontal {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['stepChange']);
    }
};
/** @nocollapse */ MarkantStepperHorizontal.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantStepperHorizontal, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantStepperHorizontal.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantStepperHorizontal, selector: "markant-stepper-horizontal", inputs: { clickable: "clickable", currentStepIndex: "currentStepIndex", numbered: "numbered", position: "position", skippable: "skippable" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantStepperHorizontal = __decorate([
    ProxyCmp({
        inputs: ['clickable', 'currentStepIndex', 'numbered', 'position', 'skippable'],
        methods: ['previousStep', 'nextStep', 'toStep']
    })
], MarkantStepperHorizontal);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantStepperHorizontal, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-stepper-horizontal',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['clickable', 'currentStepIndex', 'numbered', 'position', 'skippable'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantStepperItem = class MarkantStepperItem {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['descriptionChanged']);
    }
};
/** @nocollapse */ MarkantStepperItem.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantStepperItem, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantStepperItem.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantStepperItem, selector: "markant-stepper-item", inputs: { description: "description", substep: "substep" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantStepperItem = __decorate([
    ProxyCmp({
        inputs: ['description', 'substep']
    })
], MarkantStepperItem);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantStepperItem, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-stepper-item',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['description', 'substep'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSupportButton = class MarkantSupportButton {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSupportButton.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSupportButton, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSupportButton.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSupportButton, selector: "markant-support-button", inputs: { heading: "heading", height: "height", heightMobile: "heightMobile", round: "round", text: "text", width: "width", widthMobile: "widthMobile" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSupportButton = __decorate([
    ProxyCmp({
        inputs: ['heading', 'height', 'heightMobile', 'round', 'text', 'width', 'widthMobile']
    })
], MarkantSupportButton);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSupportButton, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-support-button',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['heading', 'height', 'heightMobile', 'round', 'text', 'width', 'widthMobile'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSupportButtonItem = class MarkantSupportButtonItem {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSupportButtonItem.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSupportButtonItem, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSupportButtonItem.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSupportButtonItem, selector: "markant-support-button-item", inputs: { arrow: "arrow", info: "info", label: "label", link: "link", target: "target" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSupportButtonItem = __decorate([
    ProxyCmp({
        inputs: ['arrow', 'info', 'label', 'link', 'target']
    })
], MarkantSupportButtonItem);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSupportButtonItem, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-support-button-item',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['arrow', 'info', 'label', 'link', 'target'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSwipe = class MarkantSwipe {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSwipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSwipe, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSwipe.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSwipe, selector: "markant-swipe", inputs: { imageSlides: "imageSlides" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSwipe = __decorate([
    ProxyCmp({
        inputs: ['imageSlides']
    })
], MarkantSwipe);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSwipe, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-swipe',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['imageSlides'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSwiper = class MarkantSwiper {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantSwiper.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSwiper, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSwiper.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSwiper, selector: "markant-swiper", inputs: { animation: "animation", animationDuration: "animationDuration", autoSlideInterval: "autoSlideInterval", endless: "endless", indicator: "indicator", initialSlide: "initialSlide", mode: "mode", navigation: "navigation", padding: "padding", size: "size" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSwiper = __decorate([
    ProxyCmp({
        inputs: ['animation', 'animationDuration', 'autoSlideInterval', 'endless', 'indicator', 'initialSlide', 'mode', 'navigation', 'padding', 'size']
    })
], MarkantSwiper);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSwiper, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-swiper',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['animation', 'animationDuration', 'autoSlideInterval', 'endless', 'indicator', 'initialSlide', 'mode', 'navigation', 'padding', 'size'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantSwitch = class MarkantSwitch {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['checkedChange']);
    }
};
/** @nocollapse */ MarkantSwitch.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSwitch, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantSwitch.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantSwitch, selector: "markant-switch", inputs: { alignment: "alignment", checked: "checked", disabled: "disabled", iconChecked: "iconChecked", iconUnchecked: "iconUnchecked", name: "name" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantSwitch = __decorate([
    ProxyCmp({
        inputs: ['alignment', 'checked', 'disabled', 'iconChecked', 'iconUnchecked', 'name']
    })
], MarkantSwitch);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantSwitch, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-switch',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['alignment', 'checked', 'disabled', 'iconChecked', 'iconUnchecked', 'name'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTab = class MarkantTab {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['interacted']);
    }
};
/** @nocollapse */ MarkantTab.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTab, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTab.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTab, selector: "markant-tab", inputs: { icon: "icon", indicatorContentWidth: "indicatorContentWidth", label: "label", stacked: "stacked" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTab = __decorate([
    ProxyCmp({
        inputs: ['icon', 'indicatorContentWidth', 'label', 'stacked']
    })
], MarkantTab);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTab, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-tab',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['icon', 'indicatorContentWidth', 'label', 'stacked'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTabBar = class MarkantTabBar {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['activeTabChange']);
    }
};
/** @nocollapse */ MarkantTabBar.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTabBar, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTabBar.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTabBar, selector: "markant-tab-bar", inputs: { activeTab: "activeTab" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTabBar = __decorate([
    ProxyCmp({
        inputs: ['activeTab']
    })
], MarkantTabBar);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTabBar, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-tab-bar',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['activeTab'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTable = class MarkantTable {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['sortChange']);
    }
};
/** @nocollapse */ MarkantTable.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTable, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTable.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTable, selector: "markant-table", inputs: { loading: "loading", noHover: "noHover", rowHeightSize: "rowHeightSize", sortColumnId: "sortColumnId", sortDirection: "sortDirection", stickyHeader: "stickyHeader", variant: "variant" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTable = __decorate([
    ProxyCmp({
        inputs: ['loading', 'noHover', 'rowHeightSize', 'sortColumnId', 'sortDirection', 'stickyHeader', 'variant']
    })
], MarkantTable);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTable, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-table',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['loading', 'noHover', 'rowHeightSize', 'sortColumnId', 'sortDirection', 'stickyHeader', 'variant'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTableHeaderCell = class MarkantTableHeaderCell {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['sortDirectionChange', 'searchFocusChange']);
    }
};
/** @nocollapse */ MarkantTableHeaderCell.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTableHeaderCell, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTableHeaderCell.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTableHeaderCell, selector: "markant-table-header-cell", inputs: { autofocus: "autofocus", columnId: "columnId", contentAlignment: "contentAlignment", label: "label", notSortable: "notSortable", numeric: "numeric", pin: "pin", searchIcon: "searchIcon", searched: "searched", sortDirection: "sortDirection", sortStart: "sortStart" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTableHeaderCell = __decorate([
    ProxyCmp({
        inputs: ['autofocus', 'columnId', 'contentAlignment', 'label', 'notSortable', 'numeric', 'pin', 'searchIcon', 'searched', 'sortDirection', 'sortStart'],
        methods: ['setSearchable', 'setFocus', 'setBlur']
    })
], MarkantTableHeaderCell);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTableHeaderCell, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-table-header-cell',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autofocus', 'columnId', 'contentAlignment', 'label', 'notSortable', 'numeric', 'pin', 'searchIcon', 'searched', 'sortDirection', 'sortStart'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTablePagination = class MarkantTablePagination {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['pageSizeChange', 'indexChange']);
    }
};
/** @nocollapse */ MarkantTablePagination.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTablePagination, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTablePagination.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTablePagination, selector: "markant-table-pagination", inputs: { goToLabel: "goToLabel", index: "index", managed: "managed", pageSize: "pageSize", selectLabel: "selectLabel", showGoTo: "showGoTo", totalCount: "totalCount" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTablePagination = __decorate([
    ProxyCmp({
        inputs: ['goToLabel', 'index', 'managed', 'pageSize', 'selectLabel', 'showGoTo', 'totalCount']
    })
], MarkantTablePagination);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTablePagination, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-table-pagination',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['goToLabel', 'index', 'managed', 'pageSize', 'selectLabel', 'showGoTo', 'totalCount'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTablePaginationSelectOption = class MarkantTablePaginationSelectOption {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantTablePaginationSelectOption.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTablePaginationSelectOption, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTablePaginationSelectOption.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTablePaginationSelectOption, selector: "markant-table-pagination-select-option", inputs: { disabled: "disabled", selected: "selected", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTablePaginationSelectOption = __decorate([
    ProxyCmp({
        inputs: ['disabled', 'selected', 'value']
    })
], MarkantTablePaginationSelectOption);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTablePaginationSelectOption, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-table-pagination-select-option',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['disabled', 'selected', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTextarea = class MarkantTextarea {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
        proxyOutputs(this, this.el, ['valueChange', 'markantFocus', 'markantBlur']);
    }
};
/** @nocollapse */ MarkantTextarea.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTextarea, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTextarea.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTextarea, selector: "markant-textarea", inputs: { autoFocus: "autoFocus", autogrow: "autogrow", cols: "cols", disabled: "disabled", error: "error", helper: "helper", helperPersistent: "helperPersistent", helperValidation: "helperValidation", label: "label", maxlength: "maxlength", minlength: "minlength", name: "name", outlined: "outlined", placeholder: "placeholder", readonly: "readonly", required: "required", resizeable: "resizeable", rows: "rows", showCharacterCounter: "showCharacterCounter", showLabelHint: "showLabelHint", useCustomLayout: "useCustomLayout", value: "value" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTextarea = __decorate([
    ProxyCmp({
        inputs: ['autoFocus', 'autogrow', 'cols', 'disabled', 'error', 'helper', 'helperPersistent', 'helperValidation', 'label', 'maxlength', 'minlength', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'resizeable', 'rows', 'showCharacterCounter', 'showLabelHint', 'useCustomLayout', 'value'],
        methods: ['setFocus', 'setBlur']
    })
], MarkantTextarea);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTextarea, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-textarea',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['autoFocus', 'autogrow', 'cols', 'disabled', 'error', 'helper', 'helperPersistent', 'helperValidation', 'label', 'maxlength', 'minlength', 'name', 'outlined', 'placeholder', 'readonly', 'required', 'resizeable', 'rows', 'showCharacterCounter', 'showLabelHint', 'useCustomLayout', 'value'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });
let MarkantTooltip = class MarkantTooltip {
    constructor(c, r, z) {
        this.z = z;
        c.detach();
        this.el = r.nativeElement;
    }
};
/** @nocollapse */ MarkantTooltip.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTooltip, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ MarkantTooltip.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.10", type: MarkantTooltip, selector: "markant-tooltip", inputs: { colorScheme: "colorScheme", delay: "delay", for: "for", label: "label", placement: "placement", trigger: "trigger", triggerTarget: "triggerTarget" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
MarkantTooltip = __decorate([
    ProxyCmp({
        inputs: ['colorScheme', 'delay', 'for', 'label', 'placement', 'trigger', 'triggerTarget']
    })
], MarkantTooltip);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantTooltip, decorators: [{
            type: Component,
            args: [{
                    selector: 'markant-tooltip',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: '<ng-content></ng-content>',
                    // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
                    inputs: ['colorScheme', 'delay', 'for', 'label', 'placement', 'trigger', 'triggerTarget'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });

const DIRECTIVES = [
    MarkantBreadcrumb,
    MarkantBreadcrumbs,
    MarkantButton,
    MarkantCallout,
    MarkantCard,
    MarkantCheckbox,
    MarkantCheckboxGroup,
    MarkantChip,
    MarkantColorPalette,
    MarkantCombiSelect,
    MarkantCombiSelectNew,
    MarkantCombiSelectOption,
    MarkantCombiSelectOptionGroup,
    MarkantCurrencyInput,
    MarkantDatepicker,
    MarkantDialog,
    MarkantDropdownGroup,
    MarkantExpansionPanel,
    MarkantExpansionPanelItem,
    MarkantFilterGroup,
    MarkantFilterSelect,
    MarkantFilterSelectOption,
    MarkantFooter,
    MarkantGuideArrow,
    MarkantGuidedTour,
    MarkantHeader,
    MarkantHeaderLogoBar,
    MarkantHighlight,
    MarkantIcon,
    MarkantIconButton,
    MarkantInput,
    MarkantInputDivided,
    MarkantInputDividedOption,
    MarkantInputFile,
    MarkantInputFileProgressbar,
    MarkantInputNew,
    MarkantInputNumber,
    MarkantLabel,
    MarkantLeistungsHeader,
    MarkantList,
    MarkantListDivider,
    MarkantListItem,
    MarkantNavDrawer,
    MarkantNavDrawerSubnav,
    MarkantNavItem,
    MarkantNotificationBadge,
    MarkantPage,
    MarkantPageHeader,
    MarkantPageOverlay,
    MarkantPagination,
    MarkantPaginationSelectOption,
    MarkantPopover,
    MarkantProgressBar,
    MarkantRadio,
    MarkantRadioGroup,
    MarkantRange,
    MarkantSearchbar,
    MarkantSearchbarCategory,
    MarkantSearchbarSuggestion,
    MarkantSegmentedControl,
    MarkantSegmentedControlGroup,
    MarkantSelect,
    MarkantSelectOption,
    MarkantSeperator,
    MarkantSkeletonScreen,
    MarkantSlide,
    MarkantSlider,
    MarkantSnackbar,
    MarkantSpinner,
    MarkantStepIndicator,
    MarkantStepperHorizontal,
    MarkantStepperItem,
    MarkantSupportButton,
    MarkantSupportButtonItem,
    MarkantSwipe,
    MarkantSwiper,
    MarkantSwitch,
    MarkantTab,
    MarkantTabBar,
    MarkantTable,
    MarkantTableHeaderCell,
    MarkantTablePagination,
    MarkantTablePaginationSelectOption,
    MarkantTextarea,
    MarkantTooltip
];

const raf = (h) => {
    if (typeof __zone_symbol__requestAnimationFrame === 'function') {
        return __zone_symbol__requestAnimationFrame(h);
    }
    if (typeof requestAnimationFrame === 'function') {
        return requestAnimationFrame(h);
    }
    return setTimeout(h);
};

let didInitialize = false;
const ConfigToken = new InjectionToken('MARKANT_ELEMENTS_CONFIG');
const appInitialize = (config, doc, zone) => {
    return () => {
        const win = doc.defaultView;
        if (win && typeof window !== 'undefined') {
            if (didInitialize) {
                return;
            }
            didInitialize = true;
            win.markantElements = win.markantElements || {};
            win.markantElements.config = config;
            const aelFn = '__zone_symbol__addEventListener' in doc.body
                ? '__zone_symbol__addEventListener'
                : 'addEventListener';
            /* eslint-disable prefer-arrow/prefer-arrow-functions */
            return applyPolyfills().then(() => {
                return defineCustomElements(win, {
                    exclude: [],
                    syncQueue: true,
                    raf,
                    jmp: (h) => zone.runOutsideAngular(h),
                    ael(elm, eventName, cb, opts) {
                        elm[aelFn](eventName, cb, opts);
                    },
                    rel(elm, eventName, cb, opts) {
                        elm.removeEventListener(eventName, cb, opts);
                    }
                });
            });
        }
    };
};

class MarkantElementsModule {
    static forRoot(config) {
        return {
            ngModule: MarkantElementsModule,
            providers: [
                {
                    provide: ConfigToken,
                    useValue: config,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: appInitialize,
                    multi: true,
                    deps: [ConfigToken, DOCUMENT, NgZone],
                },
            ],
        };
    }
}
/** @nocollapse */ MarkantElementsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantElementsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ MarkantElementsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.10", ngImport: i0, type: MarkantElementsModule, declarations: [MarkantBreadcrumb, MarkantBreadcrumbs, MarkantButton, MarkantCallout, MarkantCard, MarkantCheckbox, MarkantCheckboxGroup, MarkantChip, MarkantColorPalette, MarkantCombiSelect, MarkantCombiSelectNew, MarkantCombiSelectOption, MarkantCombiSelectOptionGroup, MarkantCurrencyInput, MarkantDatepicker, MarkantDialog, MarkantDropdownGroup, MarkantExpansionPanel, MarkantExpansionPanelItem, MarkantFilterGroup, MarkantFilterSelect, MarkantFilterSelectOption, MarkantFooter, MarkantGuideArrow, MarkantGuidedTour, MarkantHeader, MarkantHeaderLogoBar, MarkantHighlight, MarkantIcon, MarkantIconButton, MarkantInput, MarkantInputDivided, MarkantInputDividedOption, MarkantInputFile, MarkantInputFileProgressbar, MarkantInputNew, MarkantInputNumber, MarkantLabel, MarkantLeistungsHeader, MarkantList, MarkantListDivider, MarkantListItem, MarkantNavDrawer, MarkantNavDrawerSubnav, MarkantNavItem, MarkantNotificationBadge, MarkantPage, MarkantPageHeader, MarkantPageOverlay, MarkantPagination, MarkantPaginationSelectOption, MarkantPopover, MarkantProgressBar, MarkantRadio, MarkantRadioGroup, MarkantRange, MarkantSearchbar, MarkantSearchbarCategory, MarkantSearchbarSuggestion, MarkantSegmentedControl, MarkantSegmentedControlGroup, MarkantSelect, MarkantSelectOption, MarkantSeperator, MarkantSkeletonScreen, MarkantSlide, MarkantSlider, MarkantSnackbar, MarkantSpinner, MarkantStepIndicator, MarkantStepperHorizontal, MarkantStepperItem, MarkantSupportButton, MarkantSupportButtonItem, MarkantSwipe, MarkantSwiper, MarkantSwitch, MarkantTab, MarkantTabBar, MarkantTable, MarkantTableHeaderCell, MarkantTablePagination, MarkantTablePaginationSelectOption, MarkantTextarea, MarkantTooltip, BooleanValueAccessorDirective,
        FsValueAccessorDirective,
        RadioValueAccessorDirective,
        TextValueAccessorDirective,
        MarkantNavItemDirective,
        MarkantBreadcrumbDirective,
        InputDividedValueAccessorDirective,
        CombiSelectValueAccessorDirective], imports: [CommonModule, RadioControlRegistryModule], exports: [MarkantBreadcrumb, MarkantBreadcrumbs, MarkantButton, MarkantCallout, MarkantCard, MarkantCheckbox, MarkantCheckboxGroup, MarkantChip, MarkantColorPalette, MarkantCombiSelect, MarkantCombiSelectNew, MarkantCombiSelectOption, MarkantCombiSelectOptionGroup, MarkantCurrencyInput, MarkantDatepicker, MarkantDialog, MarkantDropdownGroup, MarkantExpansionPanel, MarkantExpansionPanelItem, MarkantFilterGroup, MarkantFilterSelect, MarkantFilterSelectOption, MarkantFooter, MarkantGuideArrow, MarkantGuidedTour, MarkantHeader, MarkantHeaderLogoBar, MarkantHighlight, MarkantIcon, MarkantIconButton, MarkantInput, MarkantInputDivided, MarkantInputDividedOption, MarkantInputFile, MarkantInputFileProgressbar, MarkantInputNew, MarkantInputNumber, MarkantLabel, MarkantLeistungsHeader, MarkantList, MarkantListDivider, MarkantListItem, MarkantNavDrawer, MarkantNavDrawerSubnav, MarkantNavItem, MarkantNotificationBadge, MarkantPage, MarkantPageHeader, MarkantPageOverlay, MarkantPagination, MarkantPaginationSelectOption, MarkantPopover, MarkantProgressBar, MarkantRadio, MarkantRadioGroup, MarkantRange, MarkantSearchbar, MarkantSearchbarCategory, MarkantSearchbarSuggestion, MarkantSegmentedControl, MarkantSegmentedControlGroup, MarkantSelect, MarkantSelectOption, MarkantSeperator, MarkantSkeletonScreen, MarkantSlide, MarkantSlider, MarkantSnackbar, MarkantSpinner, MarkantStepIndicator, MarkantStepperHorizontal, MarkantStepperItem, MarkantSupportButton, MarkantSupportButtonItem, MarkantSwipe, MarkantSwiper, MarkantSwitch, MarkantTab, MarkantTabBar, MarkantTable, MarkantTableHeaderCell, MarkantTablePagination, MarkantTablePaginationSelectOption, MarkantTextarea, MarkantTooltip, BooleanValueAccessorDirective,
        FsValueAccessorDirective,
        RadioValueAccessorDirective,
        TextValueAccessorDirective,
        MarkantNavItemDirective,
        MarkantBreadcrumbDirective,
        InputDividedValueAccessorDirective,
        CombiSelectValueAccessorDirective] });
/** @nocollapse */ MarkantElementsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantElementsModule, imports: [CommonModule, RadioControlRegistryModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.10", ngImport: i0, type: MarkantElementsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ...DIRECTIVES,
                        BooleanValueAccessorDirective,
                        FsValueAccessorDirective,
                        RadioValueAccessorDirective,
                        TextValueAccessorDirective,
                        MarkantNavItemDirective,
                        MarkantBreadcrumbDirective,
                        InputDividedValueAccessorDirective,
                        CombiSelectValueAccessorDirective
                    ],
                    imports: [CommonModule, RadioControlRegistryModule],
                    exports: [
                        ...DIRECTIVES,
                        BooleanValueAccessorDirective,
                        FsValueAccessorDirective,
                        RadioValueAccessorDirective,
                        TextValueAccessorDirective,
                        MarkantNavItemDirective,
                        MarkantBreadcrumbDirective,
                        InputDividedValueAccessorDirective,
                        CombiSelectValueAccessorDirective
                    ],
                }]
        }] });

/*
 * Public API Surface of elements-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BooleanValueAccessorDirective, CombiSelectValueAccessorDirective, FsValueAccessorDirective, InputDividedValueAccessorDirective, MarkantBreadcrumb, MarkantBreadcrumbDirective, MarkantBreadcrumbs, MarkantButton, MarkantCallout, MarkantCard, MarkantCheckbox, MarkantCheckboxGroup, MarkantChip, MarkantColorPalette, MarkantCombiSelect, MarkantCombiSelectNew, MarkantCombiSelectOption, MarkantCombiSelectOptionGroup, MarkantCurrencyInput, MarkantDatepicker, MarkantDialog, MarkantDropdownGroup, MarkantElementsModule, MarkantExpansionPanel, MarkantExpansionPanelItem, MarkantFilterGroup, MarkantFilterSelect, MarkantFilterSelectOption, MarkantFooter, MarkantGuideArrow, MarkantGuidedTour, MarkantHeader, MarkantHeaderLogoBar, MarkantHighlight, MarkantIcon, MarkantIconButton, MarkantInput, MarkantInputDivided, MarkantInputDividedOption, MarkantInputFile, MarkantInputFileProgressbar, MarkantInputNew, MarkantInputNumber, MarkantLabel, MarkantLeistungsHeader, MarkantList, MarkantListDivider, MarkantListItem, MarkantNavDrawer, MarkantNavDrawerSubnav, MarkantNavItem, MarkantNavItemDirective, MarkantNotificationBadge, MarkantPage, MarkantPageHeader, MarkantPageOverlay, MarkantPagination, MarkantPaginationSelectOption, MarkantPopover, MarkantProgressBar, MarkantRadio, MarkantRadioGroup, MarkantRange, MarkantSearchbar, MarkantSearchbarCategory, MarkantSearchbarSuggestion, MarkantSegmentedControl, MarkantSegmentedControlGroup, MarkantSelect, MarkantSelectOption, MarkantSeperator, MarkantSkeletonScreen, MarkantSlide, MarkantSlider, MarkantSnackbar, MarkantSpinner, MarkantStepIndicator, MarkantStepperHorizontal, MarkantStepperItem, MarkantSupportButton, MarkantSupportButtonItem, MarkantSwipe, MarkantSwiper, MarkantSwitch, MarkantTab, MarkantTabBar, MarkantTable, MarkantTableHeaderCell, MarkantTablePagination, MarkantTablePaginationSelectOption, MarkantTextarea, MarkantTooltip, RadioControlRegistry, RadioControlRegistryModule, RadioValueAccessorDirective, TextValueAccessorDirective };
